// League teams (Phase 1c) — admin builds rosters from registered players.
//
// A team is a leagueTeams doc; membership lives on the registration's teamId
// rather than an array on the team. One source of truth means a player cannot
// end up on two rosters, and the unassigned pool is just "teamId == null".
//
// Every assignment touches the registration and, when a team is removed, many
// registrations at once — so writes go through writeBatch and land together.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, doc, getDoc, addDoc,
  updateDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import {
  Box, Container, Typography, Chip, CircularProgress, Alert, Button, Card,
  CardContent, Grid, IconButton, Tooltip, TextField, MenuItem, Divider, Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import { rosterStatus, rosterShortfall } from '../utils/leagueRoster';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

export default function LeagueTeams() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [assignTo, setAssignTo] = useState({});   // regId -> teamId being chosen
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'leagues', leagueId))
      .then(s => { if (s.exists()) setLeague({ id: s.id, ...s.data() }); })
      .catch(e => setError('Could not load the league: ' + e.message));
  }, [leagueId]);

  useEffect(() => {
    const unsubT = onSnapshot(
      query(collection(db, 'leagueTeams'), where('leagueId', '==', leagueId)),
      (snap) => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }));
        setTeams(rows);
        setLoading(false);
      },
      (e) => { setError('Could not load teams: ' + e.message); setLoading(false); });
    const unsubR = onSnapshot(
      query(collection(db, 'leagueRegistrations'), where('leagueId', '==', leagueId)),
      (snap) => setRegs(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (e) => setError('Could not load registrations: ' + e.message));
    return () => { unsubT(); unsubR(); };
  }, [leagueId]);

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 4000); };

  // Handing a team to someone else. The case that forces it is a captain who
  // has gone quiet: nothing else can move a team whose captain stopped
  // answering, and the captain's own control on the league page needs them to
  // be there to use it.
  const makeCaptain = async (team, member) => {
    if (!window.confirm(
      `Make ${member.firstName} ${member.lastName} captain of ${team.name}?\n\n`
      + `${team.captainName || 'The current captain'} stays on the team as a player.`)) return;
    setBusy(true);
    try {
      const fn = httpsCallable(getFunctions(), 'manageLeagueTeam');
      await fn({ teamId: team.id, action: 'transferCaptain', registrationId: member.id });
      flash(`${member.firstName} is now the captain of ${team.name}.`);
    } catch (e) {
      setError(e.message || 'Could not transfer captaincy.');
    }
    setBusy(false);
  };

  const rosterMax = Number(league?.rosterMax) || Number(league?.format) || 0;
  // Wait-listed players are not on the league yet, so they cannot be rostered.
  const eligible = regs.filter(r => r.status === 'registered');
  const unassigned = eligible.filter(r => !r.teamId);
  const membersOf = (teamId) => eligible.filter(r => r.teamId === teamId);

  const createTeam = async (name) => {
    const n = (name || '').trim();
    if (!n) return;
    if (teams.some(t => (t.name || '').toLowerCase() === n.toLowerCase())) {
      setError(`A team called "${n}" already exists.`);
      return;
    }
    try {
      await addDoc(collection(db, 'leagueTeams'), {
        leagueId, name: n, createdAt: serverTimestamp(),
      });
      setNewTeamName('');
      flash(`Team "${n}" created.`);
    } catch (e) { setError('Could not create the team: ' + e.message); }
  };

  // Enough teams to seat everyone currently registered, at the league format.
  // Creates empty teams only — who goes where stays a human decision.
  const autoCreateTeams = async () => {
    const fmt = Number(league?.format) || 0;
    if (fmt < 1) return;
    const needed = Math.ceil(eligible.length / fmt) - teams.length;
    if (needed <= 0) { flash('There are already enough teams for the players registered.'); return; }
    setBusy(true);
    try {
      const batch = writeBatch(db);
      for (let i = 0; i < needed; i++) {
        const ref = doc(collection(db, 'leagueTeams'));
        batch.set(ref, {
          leagueId, name: `Team ${teams.length + i + 1}`, createdAt: serverTimestamp(),
        });
      }
      await batch.commit();
      flash(`Created ${needed} team${needed === 1 ? '' : 's'}.`);
    } catch (e) { setError('Could not create teams: ' + e.message); }
    setBusy(false);
  };

  const assign = async (reg, teamId) => {
    if (!teamId) return;
    const team = teams.find(t => t.id === teamId);
    if (rosterMax > 0 && membersOf(teamId).length >= rosterMax) {
      setError(`${team?.name || 'That team'} is full (${rosterMax}).`);
      return;
    }
    try {
      await updateDoc(doc(db, 'leagueRegistrations', reg.id), { teamId, teamName: team?.name || '' });
      setAssignTo(a => ({ ...a, [reg.id]: '' }));
    } catch (e) { setError('Could not assign: ' + e.message); }
  };

  const unassign = async (reg) => {
    try {
      await updateDoc(doc(db, 'leagueRegistrations', reg.id), { teamId: null, teamName: '' });
    } catch (e) { setError('Could not remove from the team: ' + e.message); }
  };

  // Deleting a team must release its players in the same write, or they would
  // point at a team that no longer exists and vanish from both lists.
  const deleteTeam = async (team) => {
    const members = membersOf(team.id);
    if (!window.confirm(
      `Delete "${team.name}"?` +
      (members.length ? ` Its ${members.length} player(s) go back to unassigned.` : '')
    )) return;
    setBusy(true);
    try {
      const batch = writeBatch(db);
      members.forEach(m => batch.update(doc(db, 'leagueRegistrations', m.id), { teamId: null, teamName: '' }));
      batch.delete(doc(db, 'leagueTeams', team.id));
      await batch.commit();
      flash(`Deleted "${team.name}".`);
    } catch (e) { setError('Could not delete the team: ' + e.message); }
    setBusy(false);
  };

  // Goes through manageLeagueTeam rather than writing here, even though an
  // admin has the rules to write directly. A team name is denormalised onto
  // registrations, fixtures AND the reservations holding the courts, and two
  // copies of that cascade would eventually disagree — this page used to update
  // only the first of the three. League admins are allowed through the same
  // callable the captain uses, so there is one implementation of the rename.
  const renameTeam = async (team) => {
    const n = window.prompt('Team name', team.name || '');
    if (n === null) return;
    const trimmed = n.trim();
    if (!trimmed || trimmed === team.name) return;
    setBusy(true);
    try {
      const fn = httpsCallable(getFunctions(), 'manageLeagueTeam');
      const { data } = await fn({ teamId: team.id, action: 'rename', name: trimmed });
      flash(`Renamed to "${data.name}".`
        + (data.fixturesUpdated ? ` ${data.fixturesUpdated} fixture(s) updated.` : ''));
    } catch (e) { setError('Could not rename: ' + (e.message || 'unknown error')); }
    setBusy(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/tournament-admin/leagues')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold">{league?.name || 'League'} — Teams</Typography>
          <Typography variant="body2" color="text.secondary">
            {teams.length} team{teams.length === 1 ? '' : 's'} · {eligible.length} registered ·{' '}
            {unassigned.length} unassigned
            {league ? ` · ${league.format}s, rosters up to ${rosterMax}` : ''}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<GroupAddIcon />} onClick={autoCreateTeams}
          disabled={busy || !eligible.length}>
          Auto-create teams
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField size="small" label="New team name" value={newTeamName}
          onChange={e => setNewTeamName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') createTeam(newTeamName); }}
          placeholder={`Team ${teams.length + 1}`} sx={{ minWidth: 220 }} />
        <Button variant="contained" startIcon={<AddCircleIcon />}
          onClick={() => createTeam(newTeamName || `Team ${teams.length + 1}`)}>
          Add Team
        </Button>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Unassigned ({unassigned.length})
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {unassigned.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Everyone registered is on a team.
                  </Typography>
                ) : unassigned.map(r => (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 140 }}>
                      <Typography variant="body2" fontWeight={600}>{r.firstName} {r.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.paymentStatus === 'paid' ? 'paid' : (r.paymentStatus || 'pending')}
                      </Typography>
                    </Box>
                    <TextField select size="small" sx={{ minWidth: 130 }}
                      value={assignTo[r.id] || ''} label="Assign to"
                      onChange={e => { setAssignTo(a => ({ ...a, [r.id]: e.target.value })); assign(r, e.target.value); }}>
                      {teams.length === 0 && <MenuItem value="" disabled>No teams yet</MenuItem>}
                      {teams.map(t => {
                        const full = rosterMax > 0 && membersOf(t.id).length >= rosterMax;
                        return (
                          <MenuItem key={t.id} value={t.id} disabled={full}>
                            {t.name}{full ? ' (full)' : ` (${membersOf(t.id).length}/${rosterMax})`}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            {teams.length === 0 ? (
              <Paper elevation={1} sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
                <Typography color="text.secondary">
                  No teams yet. Add one above, or auto-create enough for everyone registered.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {teams.map(t => {
                  const members = membersOf(t.id);
                  const full = rosterMax > 0 && members.length >= rosterMax;
                  return (
                    <Grid size={{ xs: 12, sm: 6 }} key={t.id}>
                      <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, cursor: 'pointer' }}
                              onClick={() => renameTeam(t)}>{t.name}</Typography>
                            <Chip size="small" label={`${members.length}/${rosterMax}`}
                              color={full ? 'success' : 'default'} />
                            {/* Advisory, never blocking: nobody is turned away
                                mid-sign-up over this, so the club sees it here
                                while there is still time to fix it. */}
                            {(() => {
                              const st = rosterStatus(league, members);
                              if (st.complete) return null;
                              return (
                                <Tooltip title={rosterShortfall(league, members)}>
                                  <Chip size="small" color="warning" variant="outlined"
                                    label={st.shortFemale > 0 ? 'needs a female' : 'short'}
                                    sx={{ fontSize: '0.65rem', height: 20 }} />
                                </Tooltip>
                              );
                            })()}
                            <Tooltip title="Delete team">
                              <IconButton size="small" color="error" onClick={() => deleteTeam(t)} disabled={busy}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {/* A player-run team: the captain can rename it and
                              drop players from the league page, so a name that
                              changes without an admin touching it is expected. */}
                          {t.captainUserId && (
                            <Typography variant="caption" color="text.secondary">
                              Captain: {t.captainName || 'player'}
                            </Typography>
                          )}
                          <Divider sx={{ my: 1 }} />
                          {members.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No players yet.</Typography>
                          ) : members.map(m => (
                            <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {m.firstName} {m.lastName}
                              </Typography>
                              {m.userId && m.userId !== t.captainUserId && (
                                <Tooltip title={`Make ${m.firstName} the captain`}>
                                  <IconButton size="small" onClick={() => makeCaptain(t, m)} disabled={busy}>
                                    <MilitaryTechIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove from team">
                                <IconButton size="small" onClick={() => unassign(m)}>
                                  <PersonRemoveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
